/**
 * GameDetail — everything the group thinks about one game.
 *
 * This is the payoff screen, and it is deliberately a *reader*: every number on
 * it comes out of `buildGameDetail` already shaped, and nothing here computes,
 * combines or re-weights anything. The rules that make the app worth using are
 * rules about what may be shown next to what, so they are honoured by layout:
 *
 *  - The hero figure is the directly entered "desire to play again", equal
 *    weighted per night. It is never derived from the aspects, so it sits in its
 *    own tile with its own caption saying so, above and apart from them.
 *  - Descriptive and evaluative aspects are separate labelled sections with
 *    different faces and different explanatory notes, because they answer
 *    different questions. A high Weight is not a good Weight.
 *  - Disagreement is placed directly under the headline rather than at the foot.
 *    "You split hardest on Interaction" is the sentence this product exists to
 *    produce; an appendix would be the wrong claim about its importance.
 *  - Older rubric versions are announced in words. Merging them would silently
 *    average two different questionnaires.
 *  - The heart is a binary, orthogonal answer, and an unanswered heart is drawn
 *    as silence — never as a rejection.
 *
 * There are no aspect-value filters and no UI hinting at them; that is backlog,
 * not an omission to paper over. Writes live elsewhere: the hearts are a
 * read-out here, because profile identity belongs to another screen.
 */

import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { DeleteNight } from "../../components/DeleteNight.tsx";
import { Tile } from "../../components/Tile.tsx";
import { RADAR_MIN_AXES, Radar } from "../../components/Radar.tsx";
import { Link, type ScreenProps } from "../../main.tsx";
import { formatStars, starLabel } from "../../../shared/stars.ts";
import type { Game } from "../../../shared/types.ts";
import {
  AspectRow,
  Attribution,
  BoxArt,
  DisagreementList,
  EmptyState,
  ScorePair,
  PlayerChips,
  ScreenHeader,
  Strip,
  SyncLine,
} from "./components.tsx";
import {
  buildGameDetail,
  formatDate,
  nLabel,
  nightlyCounts,
  relativeDay,
  HEADLINE_STOPS,
  type AspectRollup,
  type GameDetail as GameDetailModel,
  type HeartRow,
  type NightRadar,
  type RadarAxisRow,
  type SessionSummary,
} from "./data.ts";
import { useLibrary } from "./useLibrary.ts";

import "./game-detail.css";

const SHELF = { to: "/catalog", label: "The shelf" } as const;

export function GameDetail({ params }: ScreenProps): ReactNode {
  const id = params.id;
  const { library, status, error, reload } = useLibrary();
  const detail = useMemo(() => (id ? buildGameDetail(library, id) : null), [library, id]);

  if (!detail) {
    return (
      <>
        <ScreenHeader title="Game" back={SHELF} />
        <SyncLine />
        {status === "loading" ? (
          <p className="pb-game__quiet">Reading the shelf…</p>
        ) : status === "error" ? (
          <EmptyState
            title="The shelf would not open"
            action={
              <button type="button" className="pb-btn" onClick={reload}>
                Try again
              </button>
            }
          >
            <p>{error ?? "Local storage refused to answer."}</p>
          </EmptyState>
        ) : (
          <EmptyState
            title="No such game"
            action={
              <Link to="/catalog" className="pb-btn">
                Back to the shelf
              </Link>
            }
          >
            <p>
              Nothing on this shelf has that id. It may have been removed on another device, or the
              link may be older than the library.
            </p>
          </EmptyState>
        )}
      </>
    );
  }

  const { game, headline, overall } = detail;
  const rated = headline !== null && headline.n > 0;

  return (
    <>
      <ScreenHeader title={game.title} back={SHELF} />
      <SyncLine />

      <div className="pb-stack">
        <Tile edge="spice">
          <div className="pb-game__hero">
            <BoxArt game={game} size={88} eager />
            <div className="pb-game__heroBody">
              <div className="pb-label">Desire to play again · the nights</div>
              <ScorePair
                headline={headline}
                overall={overall}
                unit="night"
                size="hero"
                absent="No finished review yet"
              />
              <p className="pb-game__caption">
                The first is typed in directly by each rater; the second is how the evenings went,
                worked out from the topics each person rated unless they set it themselves. Every
                night counts once either way, so a six-player table cannot outvote a two-player one.
                Neither is built from the aspects below — an aspect is still a profile, never a
                score.
              </p>
            </div>
          </div>

          {rated && (
            <div className="pb-game__spread">
              <div className="pb-label">Night by night</div>
              {/* One dot per night, on the nearest whole stop — a night the
                  table split on has a fractional mean and would otherwise sit
                  on no stop at all, leaving the hero figure standing over an
                  empty track. The exact mean is still drawn as the hairline. */}
              <Strip
                counts={nightlyCounts(detail.sessions)}
                stops={HEADLINE_STOPS}
                mean={headline.mean}
                showLabels
              />
            </div>
          )}

          <Facts game={game} plays={detail.sessions.length} />
        </Tile>

        <RubricNotice detail={detail} />

        <History radar={detail.radar} />

        <section className="pb-section">
          <h2 className="pb-section__title">Where the table split</h2>
          <Tile edge="ochre">
            {detail.disagreements.length > 0 ? (
              <>
                <DisagreementList rows={detail.disagreements} />
                <p className="pb-game__caption">
                  Widest spread first. A split is the finding, not a fault in the data — it means
                  two people watched different games.
                </p>
              </>
            ) : (
              <p className="pb-game__quiet">
                Nobody has rated an aspect of this game yet, so there is nothing to disagree about.
              </p>
            )}
          </Tile>
        </section>

        <AspectSection
          kind="descriptive"
          title="What it is like"
          note="Descriptions, not judgements. A high Weight is not a good Weight, so these report the stop the table landed on most, never a bare average."
          rows={detail.descriptive}
          empty="No descriptive aspects were rated under the current rubric."
        />

        <AspectSection
          kind="evaluative"
          title="What the table thought"
          note="Judgements, each standing on its own. They are never added up: an overall score would hide exactly the argument this page is for."
          rows={detail.evaluative}
          empty="No evaluative aspects were rated under the current rubric."
        />

        <Hearts rows={detail.hearts} />

        <section className="pb-section">
          <h2 className="pb-section__title">
            Play history
            <span className="pb-section__note">{nLabel(detail.sessions.length, "night")}</span>
          </h2>
          {detail.sessions.length === 0 ? (
            <Tile sunk>
              <p className="pb-game__quiet">
                No finished review yet. A night in progress stays out of every figure here until the
                group closes it.
              </p>
            </Tile>
          ) : (
            <div className="pb-game__plays">
              {detail.sessions.map((s) => (
                <PlayEntry key={s.session.id} summary={s} gameTitle={detail.game.title} />
              ))}
            </div>
          )}
        </section>
      </div>

      <Attribution />
    </>
  );
}

// ---------------------------------------------------------------------------
// Hero facts
// ---------------------------------------------------------------------------

function playerRange(game: Game): string | null {
  const { min_players: min, max_players: max } = game;
  if (min === null && max === null) return null;
  if (min !== null && max !== null) return min === max ? `${min}` : `${min}–${max}`;
  return min !== null ? `${min}+` : `up to ${max}`;
}

/**
 * Box facts, kept visibly separate from anything the group said. BGG's weight
 * in particular is labelled as BGG's, because the rubric has its own Weight
 * aspect and conflating the two would be a quiet lie.
 */
function Facts({ game, plays }: { game: Game; plays: number }): ReactNode {
  const players = playerRange(game);
  return (
    <dl className="pb-game__facts">
      <Fact label="Plays" value={String(plays)} />
      {game.year !== null && <Fact label="Year" value={String(game.year)} />}
      {players !== null && <Fact label="Players" value={players} />}
      {game.playtime !== null && <Fact label="Playtime" value={`${game.playtime} min`} />}
      {game.weight !== null && (
        <Fact label="BGG weight" value={game.weight.toFixed(1)} hint="BoardGameGeek's community weight, not the group's Weight aspect" />
      )}
    </dl>
  );
}

function Fact({ label, value, hint }: { label: string; value: string; hint?: string }): ReactNode {
  return (
    <div className="pb-game__fact" title={hint}>
      <dt className="pb-label">{label}</dt>
      <dd className="pb-game__factValue">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The history radar
// ---------------------------------------------------------------------------

/**
 * Recency as ink. Newest night full strength, each older one fainter.
 *
 * Distinct hues were the alternative and they carry no order: five colours need
 * a legend read left to right before the picture means anything, whereas fading
 * says "older" without being looked up. The ramp bottoms out at 0.3 rather than
 * fading to nothing, because a night that is invisible is a night that may as
 * well not be plotted.
 */
const NIGHT_STRENGTH = [1, 0.76, 0.56, 0.42, 0.3];

/** The words the drawing cannot say, for anyone not reading the drawing. */
function nightWords(axis: RadarAxisRow, value: number | null): string {
  // Null covers two cases the model does not separate and the reader does not
  // need separated: the night never carried this aspect, or it did and nobody
  // gave it a value. Either way there is no answer to read out.
  if (value === null) return `${axis.name}: not rated`;
  // `starLabel` is null when the value falls between two of the rubric's stops —
  // a median of a split table does exactly that — and then the star count is the
  // honest reading of it.
  return `${axis.name}: ${starLabel(axis.stops, value) ?? formatStars(value)}`;
}

function History({ radar }: { radar: NightRadar }): ReactNode {
  const [focus, setFocus] = useState<string | null>(null);
  // Above the early returns because hooks are, and memoised because focusing a
  // night must not rebuild the nights.
  const series = useMemo(
    () =>
      radar.nights.map((night, i) => ({
        id: night.sessionId,
        label: formatDate(night.playedAt),
        // One hue for every night: the ramp carries which night it is, so a
        // second dimension of colour would be saying the same thing twice and
        // would collide with the seat colours the results screen uses for people.
        colour: "var(--pb-slate)",
        strength: NIGHT_STRENGTH[i] ?? 0.3,
        points: night.points,
      })),
    [radar.nights],
  );
  if (radar.nights.length === 0) return null;

  if (radar.axes.length < RADAR_MIN_AXES) {
    return (
      <section className="pb-section">
        <h2 className="pb-section__title">How it has landed</h2>
        <Tile sunk>
          <p className="pb-game__quiet">
            A shape needs three categories to be a shape, and this game has{" "}
            {nLabel(radar.axes.length, "answer")} worth of them so far.
          </p>
        </Tile>
      </section>
    );
  }

  return (
    <section className="pb-section">
      <h2 className="pb-section__title">
        How it has landed
        <span className="pb-section__note">
          {radar.nights.length === radar.available
            ? nLabel(radar.nights.length, "night")
            : `last ${radar.nights.length} of ${radar.available} nights`}
        </span>
      </h2>
      <Tile sunk edge="slate">
        <Radar
          axes={radar.axes}
          series={series}
          focusId={focus}
          onFocus={setFocus}
          caption="One shape per night, newest darkest. Each point is that night's median, so a table that split lands in the middle of what it split over — the blocks below still have every rater's own answer. Further out is not better."
        />
        <ul className="pb-radar__words">
          {radar.nights.map((night) => (
            <li key={night.sessionId}>
              <span className="pb-label">{formatDate(night.playedAt)}</span>
              <span className="pb-radar__wordsLine">
                {radar.axes
                  .map((axis) => nightWords(axis, night.points[axis.id] ?? null))
                  .join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      </Tile>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rubric versions
// ---------------------------------------------------------------------------

/**
 * Said out loud, never merged. The questions changed between rubric versions,
 * so answers to them are not comparable and pooling them would invent a
 * consensus nobody expressed.
 */
function RubricNotice({ detail }: { detail: GameDetailModel }): ReactNode {
  const { supersededVersions: older, rubricVersion } = detail;
  if (older.length === 0) return null;

  const inScope = detail.sessions.filter((s) => s.session.rubric_version === rubricVersion).length;
  const list = older.map((v) => `v${v}`).join(", ");

  return (
    <Tile edge="rust" title="Older rubric kept separate">
      <p className="pb-game__prose">
        This game also has reviews under {older.length === 1 ? "rubric" : "rubrics"} {list}. Those
        nights asked different questions, so their aspect answers are not merged into the profiles
        below — the profiles come from rubric v{rubricVersion} only, {nLabel(inScope, "night")}.
      </p>
      <p className="pb-game__prose pb-game__prose--aside">
        The headline above is unaffected: “desire to play again” is the same question in every
        rubric, so it spans all {detail.sessions.length} nights.
      </p>
    </Tile>
  );
}

// ---------------------------------------------------------------------------
// Aspects
// ---------------------------------------------------------------------------

function AspectSection({
  kind,
  title,
  note,
  rows,
  empty,
}: {
  kind: "descriptive" | "evaluative";
  title: string;
  note: string;
  rows: readonly AspectRollup[];
  empty: string;
}): ReactNode {
  return (
    <section className="pb-section pb-game__aspects" data-kind={kind}>
      <h2 className="pb-section__title">
        {title}
        <span className="pb-game__kindTag">{kind === "descriptive" ? "Description" : "Judgement"}</span>
      </h2>
      <p className="pb-game__prose pb-game__prose--aside">{note}</p>
      <Tile sunk={kind === "descriptive"} edge={kind === "descriptive" ? "slate" : "spice"}>
        {rows.length === 0 ? (
          <p className="pb-game__quiet">{empty}</p>
        ) : (
          rows.map((rollup) => <AspectRow key={rollup.aspectId} rollup={rollup} />)
        )}
      </Tile>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The play_again heart
// ---------------------------------------------------------------------------

const HEART_STATE = {
  yes: { glyph: "♥", word: "Would play again" },
  no: { glyph: "♡", word: "Would not" },
  quiet: { glyph: "–", word: "Never answered" },
} as const;

function heartState(row: HeartRow): keyof typeof HEART_STATE {
  return row.loved === null ? "quiet" : row.loved ? "yes" : "no";
}

export interface HeartTally {
  yes: number;
  no: number;
  /** Sat at a table for this game and never marked the heart either way. */
  quiet: number;
  /** yes + no. The population the two counts above are out of. */
  answered: number;
  /** Everyone who has played it. The population `quiet` is out of. */
  players: number;
}

/**
 * The heart counts people; the headline above counts nights.
 *
 * `players` is carried rather than left implicit because without it the section
 * could only print a bare "3 never answered" under a hero reading "n = 3
 * nights", and the two threes are different populations — three evenings above,
 * three people below. Read together they looked like a page that had lost half
 * its data.
 */
export function heartTally(rows: readonly { loved: boolean | null }[]): HeartTally {
  const yes = rows.filter((r) => r.loved === true).length;
  const no = rows.filter((r) => r.loved === false).length;
  return { yes, no, quiet: rows.length - yes - no, answered: yes + no, players: rows.length };
}

/**
 * A binary answer, held apart from the score on purpose: "I would play this
 * again" and "I rate it 7.5" are different sentences, and one is not evidence
 * for the other. Silence is drawn as silence — an unanswered heart is neither a
 * yes nor a no, and rendering it as a rejection would put words in someone's
 * mouth.
 *
 * It is also the second thing on this page with "play again" in its name, and
 * the two are counted over different populations. So every figure here says
 * whose it is out loud: the hero above is nights, this is people, and a page
 * that printed "n = 3 nights" over "3 never answered" without saying so read as
 * a page that had mislaid three answers.
 */
function Hearts({ rows }: { rows: readonly HeartRow[] }): ReactNode {
  const { yes, no, quiet, answered, players } = heartTally(rows);

  return (
    <section className="pb-section">
      <h2 className="pb-section__title">
        Would play again
        <span className="pb-section__note">Per person, not part of any score</span>
      </h2>
      <Tile>
        {rows.length === 0 ? (
          <p className="pb-game__quiet">Nobody at the table has played this one yet.</p>
        ) : (
          <>
            <p className="pb-game__prose pb-game__prose--aside">
              A standing yes or no, kept on each player&rsquo;s own profile rather than asked at
              the end of a night. It counts people, so it is not the figure at the top of this
              page — that one is the 1–10 everybody types in fresh at every table.
            </p>
            {answered === 0 ? (
              <p className="pb-game__heartSummary">
                Nobody has marked this one yet — all {players} who have played it left it blank.
              </p>
            ) : (
              <p className="pb-game__heartSummary">
                <span className="pb-game__heartCount">{yes}</span> of the {players} who have played
                it would play it again
                {no > 0 && (
                  <>
                    , <span className="pb-game__heartCount">{no}</span> would not
                  </>
                )}
                {quiet > 0 && <> · {quiet} never answered</>}
                <span className="pb-game__heartN">{nLabel(answered, "answer")}</span>
              </p>
            )}
            <ul className="pb-game__hearts">
              {rows.map((row) => {
                const state = heartState(row);
                const { glyph, word } = HEART_STATE[state];
                return (
                  <li className="pb-game__heart" key={row.profile.id} data-state={state}>
                    <span
                      className="pb-game__heartName"
                      style={{ borderLeftColor: row.profile.colour }}
                    >
                      {row.profile.name}
                    </span>
                    <span className="pb-game__heartGlyph" aria-hidden="true">
                      {glyph}
                    </span>
                    <span className="pb-game__heartWord">{word}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Tile>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Play history
// ---------------------------------------------------------------------------

function PlayEntry({
  summary,
  gameTitle,
}: {
  summary: SessionSummary;
  gameTitle: string;
}): ReactNode {
  return (
    <div className="pb-nightcard">
      <PlayLink summary={summary} />
      {/* Same as the home screen: the sync inside `cancelSession` takes the row
          out of the list this row is in, so there is nowhere to navigate to. */}
      <DeleteNight sessionId={summary.session.id} placement="corner" />
    </div>
  );
}

function PlayLink({ summary }: { summary: SessionSummary }): ReactNode {
  const { session, players, headline, overall } = summary;
  const verdict = session.verdict?.trim();
  const note = session.note?.trim();

  return (
    <Link to={`/session/${session.id}`} className="pb-game__play">
      <Tile
        interactive
        edge={session.first_play === 1 ? "ochre" : "muted"}
        title={formatDate(session.played_at)}
        meta={
          <>
            {relativeDay(session.played_at)}
            {session.first_play === 1 && <span className="pb-game__firstPlay">First play</span>}
            {session.medium === "DIGITAL" && <span className="pb-game__firstPlay">Digital</span>}
          </>
        }
      >
        {verdict && <p className="pb-game__verdict">“{verdict}”</p>}
        {/* A session's n counts raters; the game's counts nights. Same figure,
            different populations, so the unit travels with every one of them.
            The two `n`s here differ on purpose: the headline is compulsory and
            the overall is worked out from topics anybody may skip. */}
        <ScorePair
          headline={headline}
          overall={overall}
          unit="rater"
          absent="Nobody rated this night"
        />
        {note && <p className="pb-game__note">{note}</p>}
        <PlayerChips players={players} />
      </Tile>
    </Link>
  );
}
